import { APP_VERSION } from '@/utils/version';
import { Calendar, ChevronLeft, ChevronRight, FolderOpen, Home, MessageCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

interface VenereNavigationProps {
  items: NavigationItem[];
  className?: string;
  onCollapseChange?: (collapsed: boolean) => void;
  onWidthChange?: (width: number) => void;
}

export const VenereNavigation: React.FC<VenereNavigationProps> = ({ 
  items, 
  className = '',
  onCollapseChange,
  onWidthChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(false);
  const navbarRef = useRef<HTMLDivElement>(null);

  // Measure navbar width and notify parent
  useEffect(() => {
    const updateWidth = () => {
      if (navbarRef.current && !isCollapsed) {
        const width = navbarRef.current.offsetWidth;
        const scrollWidth = navbarRef.current.scrollWidth;
        const clientWidth = navbarRef.current.clientWidth;
        onWidthChange?.(width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    return () => window.removeEventListener('resize', updateWidth);
  }, [isCollapsed, onWidthChange]);

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close mobile menu if open
    if (window.innerWidth < 1024) {
      setIsExpanded(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
    
    // Notify parent of width change
    if (newCollapsedState) {
      onWidthChange?.(64); // 16 * 4 = 64px
    } else {
      // Will be measured in useEffect
    }
  };

  const toggleMobileCollapse = () => {
    setIsMobileCollapsed(!isMobileCollapsed);
  };

  return (
    <>
      {/* Mobile & Tablet Navigation - Bottom (Small and medium screens) */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 ${className}`}>
        {/* Backdrop for mobile menu */}
        {isExpanded && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setIsExpanded(false)}
          />
        )}
        
        {/* Navigation Bar */}
        <div className="venere-bg-seasalt">
          <div className="flex items-center justify-around px-4 py-3">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`relative flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 rounded-lg transition-all duration-200 ${
                  isActive(item.path)
                    ? 'venere-text-royal bg-[#0B4651]/10 shadow-sm'
                    : 'venere-text-slate hover:bg-[#0B4651]/5'
                }`}
              >
                <div className="relative">
                  <div className={`mb-1 ${isActive(item.path) ? 'venere-text-royal' : 'venere-text-slate'}`}>
                    {item.icon}
                  </div>
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                {!isMobileCollapsed && (
                  <span className="text-xs font-medium truncate max-w-full">
                    {item.label}
                  </span>
                )}
                
                {/* Active indicator */}
                {isActive(item.path) && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#0B4651] rounded-full" />
                )}
              </button>
            ))}
            
            {/* Mobile/Tablet Toggle Button */}
            <button
              onClick={toggleMobileCollapse}
              className="venere-text-royal p-2 rounded-lg hover:bg-[#768289]/10 transition-colors"
              title={isMobileCollapsed ? "Show labels" : "Hide labels"}
            >
              {isMobileCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Desktop Navigation - Left Sidebar (Large screens and above) */}
      <nav className={`hidden lg:flex fixed left-0 top-0 h-full z-40 ${className}`}>
        <div 
          ref={navbarRef}
          className={`venere-bg-seasalt transition-all duration-300 ease-in-out ${
            isCollapsed ? 'w-16' : 'w-auto min-w-[200px] max-w-[240px]'
          }`}
        >
          {/* Navigation Items */}
          <div className="p-2 space-y-1 pt-4">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`relative w-full flex items-center justify-center lg:justify-start px-3 h-12 rounded-lg transition-all duration-200 group ${
                  isActive(item.path)
                    ? 'venere-text-royal bg-[#0B4651]/10 shadow-sm'
                    : 'venere-text-slate hover:bg-[#768289]/10'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <div className="relative flex-shrink-0 flex items-center justify-center">
                  <div className={isActive(item.path) ? 'text-[#0B4651]' : 'venere-text-slate'}>
                    {item.icon}
                  </div>
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                {!isCollapsed && (
                  <span className="font-medium truncate ml-3">{item.label}</span>
                )}
                
                {/* Active indicator */}
                {isActive(item.path) && (
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-[#0B4651] rounded-r-full" />
                )}
              </button>
            ))}
          </div>

          {/* Version Indicator - Above toggle button */}
          {process.env.NODE_ENV === 'development' && (
            <div className="absolute bottom-16 left-0 right-0 px-2">
              <div className="bg-red-500 text-white text-xs px-2 py-1 rounded text-center font-medium"
                   style={{ border: '1px solid red' }}
                   title="Venere Platform - Development Mode">
                {isCollapsed ? 'v' + APP_VERSION : `v${APP_VERSION} - VENERE`}
              </div>
            </div>
          )}

          {/* Toggle Button - Inside navbar at bottom */}
          <div className="absolute bottom-4 left-0 right-0 px-2">
            <button
              onClick={toggleCollapse}
              className="venere-text-royal w-full p-2 rounded-lg hover:bg-[#768289]/10 transition-colors flex items-center justify-center"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

// Default navigation items for Venere platform with Lucide icons
export const defaultVenereNavigationItems: NavigationItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <Home size={20} />,
    path: '/venere/home',
  },
  {
    id: 'folders',
    label: 'Cartelle',
    icon: <FolderOpen size={20} />,
    path: '/venere/folders',
  },
  {
    id: 'chat',
    label: 'Chat',
    icon: <MessageCircle size={20} />,
    path: '/venere/chat',
  },
  {
    id: 'consults',
    label: 'Consulti',
    icon: <Calendar size={20} />,
    path: '/venere/consults',
  },
];

export default VenereNavigation; 